(() => {
  const instances = new WeakMap();

  class KouritenCarousel {
    constructor(root) {
      this.root = root;
      this.track = root.querySelector('[data-kouriten-carousel-track]');
      this.pages = this.track
        ? Array.from(this.track.children).filter((el) => el.classList.contains('kouriten-carousel-page'))
        : [];
      this.prevButton = root.querySelector('[data-kouriten-carousel-prev]');
      this.nextButton = root.querySelector('[data-kouriten-carousel-next]');
      this.pagination = root.querySelector('[data-kouriten-carousel-pagination]');
      this.currentPage = 0;
      this.autoplayTimer = null;
      this.resizeObserver = null;
      this.pointerStartX = null;
      this.pointerStartY = null;
      this.pointerId = null;
      this.dragging = false;
      this.destroyed = false;

      if (!this.track || this.pages.length === 0) return;

      this.init();
    }

    init() {
      this.renderPagination();

      this.prevButton?.addEventListener('click', this.onPrevious);
      this.nextButton?.addEventListener('click', this.onNext);
      this.pagination?.addEventListener('click', this.onPaginationClick);

      this.track.addEventListener('pointerdown', this.onPointerDown, { passive: true });
      this.track.addEventListener('pointermove', this.onPointerMove, { passive: false });
      this.track.addEventListener('pointerup', this.onPointerUp, { passive: true });
      this.track.addEventListener('pointercancel', this.onPointerCancel, { passive: true });

      this.root.addEventListener('mouseenter', this.pauseAutoplay);
      this.root.addEventListener('mouseleave', this.resumeAutoplay);
      this.root.addEventListener('focusin', this.pauseAutoplay);
      this.root.addEventListener('focusout', this.onFocusOut);

      this.resizeObserver = new ResizeObserver(() => this.realign());
      this.resizeObserver.observe(this.root);

      this.applyPosition(false);
      this.updateControls();
      this.resumeAutoplay();
    }

    onPrevious = (event) => {
      event.preventDefault();
      this.goTo(this.currentPage - 1, true);
    };

    onNext = (event) => {
      event.preventDefault();
      this.goTo(this.currentPage + 1, true);
    };

    onPaginationClick = (event) => {
      const button = event.target.closest('[data-kouriten-carousel-page]');
      if (!button || !this.pagination.contains(button)) return;
      event.preventDefault();
      this.goTo(Number(button.dataset.kouritenCarouselPage), true);
    };

    goTo(index, smooth = true) {
      if (this.pages.length <= 1) return;

      const next = Math.max(0, Math.min(index, this.pages.length - 1));
      this.currentPage = next;
      this.applyPosition(smooth);
      this.updateControls();
      this.restartAutoplay();
    }

    applyPosition(smooth) {
      if (!this.track) return;

      this.track.style.transition = smooth
        ? 'transform 320ms ease'
        : 'none';
      this.track.style.transform = `translate3d(${-this.currentPage * 100}%, 0, 0)`;
      this.updateAccessibility();
    }

    realign = () => {
      if (this.destroyed) return;
      this.applyPosition(false);
    };

    renderPagination() {
      if (!this.pagination || this.pages.length <= 1) {
        if (this.pagination) this.pagination.hidden = true;
        return;
      }

      this.pagination.hidden = false;
      this.pagination.innerHTML = this.pages
        .map((_, index) => `
          <button
            type="button"
            class="kouriten-carousel__dot"
            data-kouriten-carousel-page="${index}"
            aria-label="${index + 1}"
            aria-current="${index === 0 ? 'true' : 'false'}"
          ></button>
        `)
        .join('');
    }

    updateControls() {
      const hasMultiplePages = this.pages.length > 1;

      if (this.prevButton) {
        this.prevButton.disabled = !hasMultiplePages;
        this.prevButton.setAttribute('aria-disabled', String(!hasMultiplePages));
      }

      if (this.nextButton) {
        this.nextButton.disabled = !hasMultiplePages;
        this.nextButton.setAttribute('aria-disabled', String(!hasMultiplePages));
      }

      const current = this.root.querySelector('[data-kouriten-carousel-current]');
      const total = this.root.querySelector('[data-kouriten-carousel-total]');

      if (current) current.textContent = String(this.currentPage + 1);
      if (total) total.textContent = String(this.pages.length);

      this.pagination?.querySelectorAll('[data-kouriten-carousel-page]').forEach((button, index) => {
        button.setAttribute('aria-current', String(index === this.currentPage));
      });
    }

    updateAccessibility() {
      this.pages.forEach((page, index) => {
        const active = index === this.currentPage;
        page.setAttribute('aria-hidden', String(!active));
        page.inert = !active;
      });
    }

    onPointerDown = (event) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      if (this.pages.length <= 1) return;

      this.pointerId = event.pointerId;
      this.pointerStartX = event.clientX;
      this.pointerStartY = event.clientY;
      this.dragging = false;
      this.pauseAutoplay();
    };

    onPointerMove = (event) => {
      if (this.pointerId !== event.pointerId || this.pointerStartX === null) return;

      const deltaX = event.clientX - this.pointerStartX;
      const deltaY = event.clientY - this.pointerStartY;

      if (!this.dragging && Math.abs(deltaX) < 8) return;
      if (Math.abs(deltaY) > Math.abs(deltaX)) return;

      this.dragging = true;
      event.preventDefault();
    };

    onPointerUp = (event) => {
      if (this.pointerId !== event.pointerId) return;

      const deltaX = event.clientX - this.pointerStartX;
      const width = this.root.getBoundingClientRect().width || 1;
      const threshold = Math.min(100, width * 0.18);

      if (this.dragging && Math.abs(deltaX) >= threshold) {
        this.goTo(this.currentPage + (deltaX < 0 ? 1 : -1), true);
      } else {
        this.applyPosition(true);
        this.resumeAutoplay();
      }

      this.resetPointer();
    };

    onPointerCancel = () => {
      this.applyPosition(true);
      this.resetPointer();
      this.resumeAutoplay();
    };

    resetPointer() {
      this.pointerId = null;
      this.pointerStartX = null;
      this.pointerStartY = null;
      this.dragging = false;
    }

    pauseAutoplay = () => {
      if (this.autoplayTimer) {
        clearTimeout(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    };

    resumeAutoplay = () => {
      if (this.destroyed || this.pages.length <= 1) return;
      if (this.root.getClientRects().length === 0) return;
      if (this.root.matches(':hover') || this.root.contains(document.activeElement)) return;
      this.scheduleAutoplay();
    };

    restartAutoplay() {
      this.pauseAutoplay();
      this.scheduleAutoplay();
    }

    scheduleAutoplay() {
      if (this.destroyed || this.pages.length <= 1) return;
      if (this.root.getClientRects().length === 0) return;
      if (this.root.matches(':hover') || this.root.contains(document.activeElement)) return;

      const delay = Number(this.root.dataset.autoplayDelay || 8000);
      if (!Number.isFinite(delay) || delay <= 0) return;

      this.autoplayTimer = setTimeout(() => {
        this.goTo(this.currentPage + 1 >= this.pages.length ? 0 : this.currentPage + 1, true);
      }, delay);
    }

    onFocusOut = (event) => {
      if (this.root.contains(event.relatedTarget)) return;
      this.resumeAutoplay();
    };

    destroy() {
      if (this.destroyed) return;
      this.destroyed = true;
      this.pauseAutoplay();
      this.resizeObserver?.disconnect();

      this.prevButton?.removeEventListener('click', this.onPrevious);
      this.nextButton?.removeEventListener('click', this.onNext);
      this.pagination?.removeEventListener('click', this.onPaginationClick);
      this.track.removeEventListener('pointerdown', this.onPointerDown);
      this.track.removeEventListener('pointermove', this.onPointerMove);
      this.track.removeEventListener('pointerup', this.onPointerUp);
      this.track.removeEventListener('pointercancel', this.onPointerCancel);
      this.root.removeEventListener('mouseenter', this.pauseAutoplay);
      this.root.removeEventListener('mouseleave', this.resumeAutoplay);
      this.root.removeEventListener('focusin', this.pauseAutoplay);
      this.root.removeEventListener('focusout', this.onFocusOut);
    }
  }

  function initAll(scope = document) {
    scope.querySelectorAll('.kouriten-carousel').forEach((root) => {
      if (instances.has(root)) return;
      const instance = new KouritenCarousel(root);
      if (instance.track) instances.set(root, instance);
    });
  }

  function destroyAll(scope = document) {
    scope.querySelectorAll('.kouriten-carousel').forEach((root) => {
      const instance = instances.get(root);
      if (!instance) return;
      instance.destroy();
      instances.delete(root);
    });
  }

  function init() {
    initAll();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    destroyAll(event.target);
  });
})();
